import { db } from '../db';

/**
 * MetricsEngine Service
 * Calculates 25+ "Best-in-Class" KPIs from local Dexie DB.
 * Supports Daily, Weekly, Monthly, Quarterly, YTD timeframes.
 */
export const metricsEngine = {

    /**
     * Calculate all metrics for a given timeframe
     * @param {string} timeframe - 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'ytd'
     * @returns {Promise<Object>} - The comprehensive metrics object
     */
    async calculateMetrics(timeframe = 'monthly') {
        const startDate = this.getStartDate(timeframe);
        const now = new Date();

        // Fetch Raw Data
        const [
            invoices,
            expenses,
            leads,
            deals,
            activities,
            users,
            domains,
            finPlan
        ] = await Promise.all([
            this.fetchData('Invoices_DB'),
            this.fetchData('Business_Expenses'),
            this.fetchData('CRM_Leads'),
            this.fetchData('CRM_Deals'),
            this.fetchData('CRM_Activities'),
            this.fetchData('Users'),
            this.fetchData('Domains_DB'),
            this.fetchData('Financial_Plan')
        ]);

        // --- FILTERING ---
        // Filter financial transactions by date
        const periodInvoices = Array.isArray(invoices) ? invoices.filter(i => new Date(i.Issue_Date || i.Date) >= startDate) : [];
        const periodExpenses = Array.isArray(expenses) ? expenses.filter(e => new Date(e.Date) >= startDate) : [];
        const periodLeads = Array.isArray(leads) ? leads.filter(l => new Date(l.Last_Contact_Date || l.Date_Added) >= startDate) : [];
        const periodDeals = Array.isArray(deals) ? deals.filter(d => new Date(d.Expected_Close_Date || d.Date) >= startDate) : [];
        const periodActivities = Array.isArray(activities) ? activities.filter(a => new Date(a.Date) >= startDate) : [];

        // --- 1. FINANCIAL FLOWS ---
        const revenues = this.sum(periodInvoices.filter(i => i.Status === 'Paid' || i.Invoice_Type === 'Receipt'), 'Total_Amount');
        const paymentsReceived = revenues; // Cash basis assumption for 'Payments Received' vs 'Revenue' (Accrual) - simplifying for now

        const totalOutflows = this.sum(periodExpenses, 'Amount');
        // Budgeted vs Unbudgeted logic (Mock logic: explicit 'Unbudgeted' tag or category match)
        // For MVP: Category 'Unforeseen' or 'Emergency' = Unbudgeted.
        const unbudgetedOutflows = this.sum(periodExpenses.filter(e => e.Category === 'Unforeseen' || e.Category === 'Emergency'), 'Amount');
        const budgetedOutflows = totalOutflows - unbudgetedOutflows;

        const debtors = this.sum(invoices.filter(i => i.Status === 'Sent' || i.Status === 'Overdue'), 'Balance'); // All time outstanding
        const creditors = 0; // Placeholder: Need 'Bills' table or Unpaid Expenses

        const netProfit = revenues - totalOutflows;
        const profitMargin = revenues > 0 ? (netProfit / revenues) * 100 : 0;

        // Cash Balance & Reserves (Simulated from Plan + Actuals)
        // In real app, this needs a 'Bank_Balances' snapshot or calculation from Genesis
        let cashBalance = 10000; // Simulated Start
        // Add all-time flows
        const allTimeRev = this.sum(invoices.filter(i => i.Status === 'Paid'), 'Total_Amount');
        const allTimeExp = this.sum(expenses, 'Amount');
        cashBalance += (allTimeRev - allTimeExp);

        // Allocations (Reserved)
        const salaryPlan = finPlan.find(p => p.Category === 'Salaries') || { Reserve_Balance: 0 };
        const capitalPlan = finPlan.find(p => p.Category === 'Capital') || { Reserve_Balance: 0 };
        const incentivePlan = finPlan.find(p => p.Category === 'Incentives') || { Reserve_Balance: 0 };
        const investPlan = finPlan.find(p => p.Category === 'Investment') || { Reserve_Balance: 0 };

        // --- 2. CRM & SALES ---
        const hotlist = leads.filter(l => l.Status === 'Hot' || l.Status === 'Qualified').length; // Snapshot, not period
        const prospectingOffers = periodActivities.filter(a => a.Type === 'Offer Sent').length;
        const mqls = periodLeads.filter(l => l.Status === 'MQL' || l.Status === 'New').length;
        const discoveryCalls = periodActivities.filter(a => a.Type?.includes('Discovery')).length;
        const demos = periodActivities.filter(a => a.Type?.includes('Demo') || a.Type?.includes('Presentation')).length;

        const sqls = periodLeads.filter(l => l.Status === 'SQL').length;
        const sqlValue = this.sum(periodDeals.filter(d => d.Stage === 'Qualification' || d.Stage === 'Discovery'), 'Deal_Value'); // Approx

        const quotesCount = periodInvoices.filter(i => i.Invoice_Type === 'Quote').length;

        // --- 3. OPS & GROWTH ---
        const newCustomers = users.filter(u => new Date(u.Join_Date) >= startDate && u.User_Role === 'CLIENT').length;
        const newDomains = domains.filter(d => new Date(d.Registration_Date || d.Expiry_Date) >= startDate).length; // Approx
        const totalDomains = domains.length;

        return {
            financials: {
                revenues,
                grossMargin: profitMargin,
                burnRate: totalOutflows, // For the selected period
                runway: totalOutflows > 0 ? (cashBalance / totalOutflows) : 999, // Months
                debtors,
                creditors,
                paymentsReceived,
                budgetedOutflows,
                unbudgetedOutflows,
                totalOutflows,
                cashBalance,
                netProfit,
                salariesReserved: salaryPlan.Reserve_Balance || 0,
                capitalReserves: capitalPlan.Reserve_Balance || 0,
                incentivesReserved: incentivePlan.Reserve_Balance || 0,
                investmentsReserved: investPlan.Reserve_Balance || 0,
                interestIncome: 0 // Placeholder
            },
            networkStats: {
                coachingClients: users.filter(u => u.User_Role === 'CLIENT' && (u.Coaching_Stream === 'Coaching' || u.Coaching_Stream === 'Executive')).length,
                selfPacedClients: users.filter(u => u.User_Role === 'CLIENT' && u.Coaching_Stream === 'Self-Paced').length,
                coaches: users.filter(u => u.User_Role === 'COACH' || u.Org_Role === 'Coach').length,
                partners: users.filter(u => u.User_Role === 'PARTNER').length,
                totalUsers: users.length
            },
            crm: {
                hotlistCount: hotlist,
                prospectingOffers,
                mqls,
                discoveryCalls,
                demos,
                sqls,
                sqlValue,
                quotesCount
            },
            ops: {
                newCustomers,
                newDomains,
                totalDomains
            }
        };
    },

    // --- Helpers ---

    getStartDate(timeframe) {
        const now = new Date();
        const d = new Date(now); // clone

        switch (timeframe) {
            case 'daily': d.setHours(0, 0, 0, 0); break;
            case 'weekly': d.setDate(d.getDate() - 7); break;
            case 'monthly': d.setDate(1); d.setHours(0, 0, 0, 0); break;
            case 'quarterly': d.setMonth(d.getMonth() - 3); break;
            case 'ytd': d.setMonth(0); d.setDate(1); break;
            default: d.setDate(1); // Default monthly
        }
        return d;
    },

    async fetchData(tabName) {
        try {
            if (!db || !db.tabData) {
                console.warn(`MetricsEngine: DB or tabData missing. Returning empty array for ${tabName}`);
                return [];
            }
            const entry = await db.tabData.where('tabName').equals(tabName).first();
            return entry?.data || [];
        } catch (e) {
            console.warn(`MetricsEngine: Could not fetch ${tabName}`, e);
            return [];
        }
    },

    sum(data, field) {
        if (!data || !Array.isArray(data)) {return 0;}
        return data.reduce((acc, item) => {
            const val = parseFloat(String(item[field] || 0).replace(/[^-0-9.]/g, ''));
            return acc + (isNaN(val) ? 0 : val);
        }, 0);
    }
};
